import { EnvironmentProviders, InjectionToken, makeEnvironmentProviders, provideAppInitializer, inject } from "@angular/core";
import { ChatbotDomains, assertValidDomainRules } from "./models/domain";
import { CHATBOT_SURFACES, ChatbotSurface, isChatbotSurface } from "./models/surface";
import { ChatbotTranslationsService } from "./services/translations.service";

/**
 * Everything this widget needs from its host, in one object.
 *
 * All of it describes the product and the deployment, not the widget: which backend answers,
 * which audiences that backend knows, and how this copy of the widget is mounted. None of it has
 * a default. A missing or half written configuration stops the host at startup instead of
 * resolving to the values that happened to suit the first product this widget served.
 */
export interface ChatbotConfig {
    /** Base URL of the chatbot API. It is also the only destination credentials are ever sent to. */
    apiUrl: string;

    /** How this copy of the widget is mounted. */
    surface: ChatbotSurface;

    /**
     * The host's own primary colour, which the widget takes on.
     *
     * Required, and without a default on purpose: each product has its own, and a widget that
     * kept the colour of whichever theme it was built against would look like a stranger in every
     * host but one. Every other shade the widget uses (hover, active, the text drawn on top) is
     * derived from this one by the theme, so this single value is the whole answer.
     *
     * Any colour notation the browser understands, since it is handed to CSS as written. A host
     * that already states its colour in a variable of its own passes `"var(--app-primary-color)"`,
     * keeping that stylesheet the single place the colour is decided. Name a variable the widget
     * does not itself declare: `var(--kendo-color-primary)` would point at the property the widget
     * sets on its own root, and resolve to nothing.
     */
    primaryColor: string;

    /**
     * The host's base text size, which the widget writes at.
     *
     * Optional, and meant to be a reference rather than a measurement: a host that already states
     * its size in a variable of its own passes `"var(--app-font-size)"`, keeping that stylesheet
     * the single place the size is decided. Left out, the widget writes at `1rem`, the host page's
     * own root size.
     *
     * Name a variable the widget does not itself declare. `var(--kendo-font-size)` would resolve
     * against the widget's own root, where the theme declares it, so it would read the theme's
     * value back rather than the host's.
     */
    fontSize?: string;

    /**
     * The domains this host serves, keyed by the name that travels to the backend.
     *
     * Usually one: the domain the host mounts the widget for. A host that also lets a tester hold
     * a conversation as another domain's user declares those domains here too, each with the
     * login URL that credential comes from.
     *
     * The names are not checked against anything on this side. They are sent as written and the
     * backend refuses the ones it does not know, so this list has to be the one that backend
     * accepts for this product.
     */
    domains: ChatbotDomains;

    /**
     * Origins allowed to hand this widget a session token over `postMessage`. Anything else
     * posting one is ignored.
     *
     * Every host with a credential needs this, not only the one across a frame boundary: the
     * handover is a message either way, and an embedding host posting on its own window is only
     * answered because its origin is listed. An iframe host lists the page that embeds it. The
     * anonymous domain never uses the channel at all, so the standalone application leaves it
     * empty.
     */
    allowedHostOrigins: string[];

    /**
     * Permits credentials to travel over plain HTTP. Local development only.
     *
     * Absent means off, which is the safe direction: with it off, a request to an `http:` URL is
     * sent without the identity header and without the bearer token, so it fails loudly at the
     * backend instead of putting a live session token on the wire in the clear. A host must only
     * ever wire this to the flag that already marks its own local build.
     */
    allowInsecureTransport?: boolean;
}

/**
 * The host's configuration, as the library reads it.
 *
 * Injected directly only by the plumbing that cannot go through a service (the interceptor, the
 * base of the API services). Everything else asks `IdentityService`, which already knows which
 * domain is current.
 */
export const CHATBOT_CONFIG = new InjectionToken<ChatbotConfig>("CHATBOT_CONFIG");

/**
 * The single entry point a host calls to configure this library.
 *
 * The configuration is validated here, while the host's providers are being built, so a mistake
 * surfaces as a named error at startup rather than as a request the backend answers 401 to, or
 * as an audience quietly served the wrong agent.
 */
export function provideChatbot(config: ChatbotConfig): EnvironmentProviders {
    return makeEnvironmentProviders([
        { provide: CHATBOT_CONFIG, useValue: validateChatbotConfig(config) },
        // The widget's own strings, merged into whatever the host already has. A host declares
        // none of them, and overrides the ones it wants: see `ChatbotTranslationsService`.
        provideAppInitializer(() => inject(ChatbotTranslationsService).start()),
    ]);
}

/**
 * Returns the configuration untouched, or throws naming the field that is wrong.
 *
 * Exported because it is also what the tests assert against: "an incomplete configuration is
 * refused" is a rule of this library, not an implementation detail of `provideChatbot`.
 */
export function validateChatbotConfig(config: ChatbotConfig): ChatbotConfig {
    if (!config || typeof config !== "object") {
        throw new Error("Chatbot: no configuration was provided. A host must call provideChatbot().");
    }

    if (typeof config.apiUrl !== "string" || config.apiUrl.trim() === "") {
        throw new Error("Chatbot: configuration needs an apiUrl.");
    }

    let apiUrl: URL;
    try {
        apiUrl = new URL(config.apiUrl);
    } catch {
        throw new Error(`Chatbot: apiUrl "${config.apiUrl}" is not an absolute URL.`);
    }

    if (!isChatbotSurface(config.surface)) {
        throw new Error(`Chatbot: configuration needs a surface, one of ${CHATBOT_SURFACES.join(", ")}.`);
    }

    if (typeof config.primaryColor !== "string" || config.primaryColor.trim() === "") {
        throw new Error("Chatbot: configuration needs a primaryColor, the host's own.");
    }

    // Asked of the browser rather than matched against a pattern of our own: it is the browser
    // that has to read this value, and it understands notations a pattern here would refuse.
    //
    // A `var(...)` reference is exempt, and has to be: it names a custom property of the host's
    // document, whose value is not known here and which `CSS.supports` reports as invalid on its
    // own. Pointing at the host's own variable is the recommended way to pass this, so refusing it
    // would refuse the good case.
    const namesHostProperty = config.primaryColor.trim().startsWith("var(");
    if (!namesHostProperty && typeof CSS !== "undefined" && CSS.supports && !CSS.supports("color", config.primaryColor)) {
        throw new Error(`Chatbot: primaryColor "${config.primaryColor}" is not a colour the browser understands.`);
    }

    if (config.fontSize !== undefined && (typeof config.fontSize !== "string" || config.fontSize.trim() === "")) {
        throw new Error("Chatbot: fontSize must be a non-empty CSS length when it is given at all.");
    }

    if (config.allowInsecureTransport !== undefined && typeof config.allowInsecureTransport !== "boolean") {
        throw new Error("Chatbot: allowInsecureTransport must be a boolean when it is given at all.");
    }

    // A plain HTTP API with the opt in left off is the one combination that breaks silently:
    // every request goes out stripped of its credentials and the backend answers 401, which
    // reads like a broken login rather than like a missing flag.
    if (apiUrl.protocol === "http:" && !config.allowInsecureTransport) {
        throw new Error(`Chatbot: apiUrl "${config.apiUrl}" is not TLS, so credentials would be withheld from every request. A local build must also set allowInsecureTransport.`);
    }

    if (!Array.isArray(config.allowedHostOrigins) || config.allowedHostOrigins.some((origin) => typeof origin !== "string" || origin.trim() === "")) {
        throw new Error("Chatbot: allowedHostOrigins must be an array of origins, empty if the host passes credentials directly.");
    }

    if (!config.domains || typeof config.domains !== "object") {
        throw new Error("Chatbot: configuration needs a domains map.");
    }

    const names = Object.keys(config.domains);
    if (names.length === 0) {
        throw new Error("Chatbot: configuration declares no domain. Declare at least the one this host mounts the widget for.");
    }

    for (const name of names) {
        assertValidDomainRules(name, config.domains[name]);

        // A sign in carries a password, and base64 is an encoding rather than a protection, so it
        // answers to the same rule as everything else this widget sends: not over plain HTTP,
        // unless the build has said out loud that it is local.
        const login = config.domains[name].login;
        if (login && new URL(login.url).protocol !== "https:" && !config.allowInsecureTransport) {
            throw new Error(`Chatbot: domain "${name}" signs in over a connection that is not TLS, and a password would travel in the clear.`);
        }
    }

    return config;
}
